import { BonusStatus } from '@atlas/bonuses';

/**
 * SDK entities expose `Date` objects for timestamps. The UI reads them as epoch ms
 */
const toEpochMs = value => {
  if (value == null || value === '') return undefined;
  const ms = value instanceof Date ? value.getTime() : Number(value) || new Date(value).getTime();
  return Number.isFinite(ms) ? ms : undefined;
};

const resolveImage = (entity, language) => {
  if (typeof entity?.getImage !== 'function') return undefined;
  try {
    return entity.getImage(language, entity.defaultLanguage) ?? undefined;
  } catch {
    return undefined;
  }
};

/**
 * Name/description/highlight are per-language in localizedInfoList (the top-level freeSpin
 * fields are unlocalized defaults). Resolve by current language, falling back to the first
 * entry - same pattern as FreeSpinCard, FreeSpinDetailsPopup and the bonus detail builder.
 */
const resolveLocalizedInfo = (localizedInfoList, language) =>
  localizedInfoList.find(info => info.language === language) || localizedInfoList[0];

/**
 * Template bonus from `bonus:search`. Templates don't have an instance id/status yet
 */
export const mapAvailableBonusForUi = (template, language) => ({
  bonusAssignmentTime: toEpochMs(template.bonusAssignmentTime),
  bonusStatus: template.status ?? BonusStatus.ACTIVE,
  defaultLanguage: template.defaultLanguage,
  description: template.description,
  endTime: toEpochMs(template.endTime),
  expirationTime: toEpochMs(template.expirationTime),
  financeDataList: template.financeDataList,
  gameIds: template.gameIds,
  id: template.id,
  imageLink: resolveImage?.(template, language),
  maxWagerAmount: template.maxWagerAmount,
  minWagerAmount: template.minWagerAmount,
  name: template.name,
  nameHighlighted: template.nameHighlighted,
  productType: template.productType,
  requiredBetsToWager: template.requiredBetsToWager,
  sportLimit: template?.sportLimit,
  timeToDeposit: template.timeToDeposit,
  timeToWager: template.timeToWager,
  type: template.type,
  wager: template.wager,
  wagerFreeSpinInstanceId: template.wagerFreeSpinInstanceId,
});

/**
 * Bonus instance from `client-bonus:search` or `client-bonus-activation-ready:search`. Flat shape - no nested template fields under .bonus
 */
export const mapClientBonusForUi = (instance, language) => ({
  bonusAmount: instance.bonusAmount,
  bonusAssignmentTime: toEpochMs(instance.bonusAssignmentTime),
  bonusTemplateId: instance.bonusId,
  copiedBonusSettings: instance.copiedBonusSettings,
  createTime: toEpochMs(instance.createTime),
  currency: instance.currency,
  description: instance.description,
  endTime: toEpochMs(instance.endTime),
  expirationTime: toEpochMs(instance.expirationTime),
  financeDataList: instance.financeDataList,
  gameIds: instance.gameIds,
  id: instance.id,
  imageLink: resolveImage?.(instance, language),
  maxWagerAmount: instance.maxWagerAmount,
  minWagerAmount: instance.minWagerAmount,
  name: instance.name,
  nameHighlighted: instance.nameHighlighted,
  notAllowedCountries: instance.notAllowedCountries,
  notAllowedPaymentMethods: instance.notAllowedPaymentMethods,
  productType: instance.productType,
  requiredBetsToWager: instance.requiredBetsToWager,
  requiredWagering: instance.requiredWagering,
  sportLimit: instance?.sportLimit,
  status: instance.status,
  timeToDeposit: instance.timeToDeposit,
  timeToWager: instance.timeToWager,
  type: instance.type,
  wager: instance.wager,
  wagerStartTime: toEpochMs(instance.wagerStartTime),
  wageredBets: instance.wageredBets,
  wagering: instance.wagering,
});

export const mapFreeSpinForUi = (freeSpin, language) => {
  const localizedInfoList = freeSpin._localizedInfoList || [];
  const localizedInfo = resolveLocalizedInfo(localizedInfoList, language);

  return {
    bonusId: freeSpin.bonusId,
    createTime: toEpochMs(freeSpin.createTime),
    currency: freeSpin.currency,
    defaultLanguage: freeSpin.defaultLanguage,
    expirationTime: toEpochMs(freeSpin.expirationTime),
    freeSpin: {
      casinoLimit: { gameIds: [...(freeSpin.gameIds || [])] },
      description: localizedInfo?.description || freeSpin.description,
      localizedImageInfoList: freeSpin._localizedImageInfoList || [],
      localizedInfoList,
      name: localizedInfo?.name || freeSpin.name,
      // When a localized entry exists, honour its nameHighlighted even if null (no highlight
      // for that locale); only fall back to the raw value when there's no localized entry.
      nameHighlighted: localizedInfo ? localizedInfo.nameHighlighted : freeSpin.nameHighlighted,
      numberOfSpins: freeSpin.numberOfSpins,
      timeToUse: freeSpin.timeToUse,
    },
    freeSpinAmount: freeSpin.freeSpinAmount,
    // Per-currency finance maps - UI picks by the freespin currency.
    // freeSpinTotalMaxWin needs the SDK FreeSpin field from FT-8533 (else undefined -> Max win hidden).
    freeSpinAmounts: freeSpin.freeSpinAmounts,
    freeSpinMinAmountsDeposit: freeSpin.freeSpinMinAmountsDeposit,
    freeSpinSubType: freeSpin.subType,
    freeSpinTotalMaxWin: freeSpin.freeSpinTotalMaxWin,
    id: freeSpin.id,
    imageLink: resolveImage?.(freeSpin, language),
    productType: freeSpin.productType,
    status: freeSpin.status,
  };
};
